import { getPropertyDisturberVariants } from '@pkgs/components/utils/tracking';
import { DEFAULT_OPTIONS } from '@pkgs/api/utils/filters';
import { isRestrictedListing, isUserAccountExclusiveListing, type GenericExposeListing } from '@pkgs/api';
import { type MeasurementSystem, PlaceType, type Listing, type MasterDataShopResponse } from '@generated/search-bff';
import { type InputContact } from '@generated/search-api';
import {
  getConsentDocumentContext,
  type getPropertyContext,
  type getLocationContext,
  type getShopContext,
  type getUserContext,
} from '@ev-tech/snowplow-library';

import { appendSuffixToKeys } from './searchFilterMappingUtils';
import { getPlaceIdForLocationContext } from './propertyDataUtils';
import { getPriceDetailsBySettings } from './getPriceDetailsBySettings';

export const mapListingToPropertyContext = (
  listing: GenericExposeListing,
  measurementSystem: MeasurementSystem,
): Parameters<typeof getPropertyContext>[0] => {
  if (isRestrictedListing(listing) || isUserAccountExclusiveListing(listing)) {
    return {
      propertyId: listing.displayId,
      bffObjectId: listing.id,
    };
  }

  const nonRestrictedListing = listing as Listing;

  return {
    propertyId: nonRestrictedListing.displayId,
    bffObjectId: nonRestrictedListing.id,
    type: nonRestrictedListing.propertyType || '',
    subType: nonRestrictedListing.propertySubType,
    constructionYear: nonRestrictedListing.constructionYear?.min,
    currency: nonRestrictedListing.baseCurrency,
    price: getPriceDetailsBySettings(
      nonRestrictedListing,
      nonRestrictedListing.basePrice ?? {},
      DEFAULT_OPTIONS.language,
      nonRestrictedListing.baseCurrency ?? DEFAULT_OPTIONS.currency,
    ).value,
    measurementUnit: nonRestrictedListing.baseUnit,
    ...(nonRestrictedListing.area && appendSuffixToKeys(nonRestrictedListing.area, measurementSystem)),
    rooms: nonRestrictedListing.rooms?.min,
    bathrooms: nonRestrictedListing.bathrooms?.min,
    bedrooms: nonRestrictedListing.bedrooms?.min,
    propertyCondition: nonRestrictedListing.condition,
    hasKitchen: nonRestrictedListing.hasBuiltInKitchen,
    hasAlarmSystem: nonRestrictedListing.hasSecuritySystem,
    hasGarden: nonRestrictedListing.hasGarden,
    hasTerrace: nonRestrictedListing.hasTerrace,
    hasBalcony: nonRestrictedListing.hasBalcony,
    hasCellar: nonRestrictedListing.hasBasement,
    hasAirconditioning: nonRestrictedListing.hasAirConditioning,
    hasPatio: nonRestrictedListing.hasPatio,
    hasSeaview: nonRestrictedListing.hasSeaOrLakeView,
    hasTennisYard: nonRestrictedListing.hasTennisCourt,
    hasParking: nonRestrictedListing.hasGarage,
    hasPool: nonRestrictedListing.hasSwimmingPool,
    isWaterfront: nonRestrictedListing.hasWaterfront,
    hasSquashCourt: nonRestrictedListing.hasSquashCourt,
    hasClubHouse: nonRestrictedListing.hasClubhouse,
    hasGolfCourse: nonRestrictedListing.hasGolfCourse,
    hasPlayground: nonRestrictedListing.hasPlayground,
    isWheelchairAccessible: nonRestrictedListing.isWheelchairAccessible,
    hasBuiltinWardrobe: nonRestrictedListing.hasBuiltInWardrobe,
    isFurnished: nonRestrictedListing.isFurnished,
    hasGuestToilet: nonRestrictedListing.hasGuestToilet,
    isSeniorOriented: nonRestrictedListing.isSeniorOriented,
    hasOpenView: nonRestrictedListing.hasOpenView,
    hasGreenView: nonRestrictedListing.hasGreenView,
    hasMountainView: nonRestrictedListing.hasMountainView,
    hasJacuzzi: nonRestrictedListing.hasJacuzzi,
    hasSauna: nonRestrictedListing.hasSauna,
    hasGym: nonRestrictedListing.hasGym,
    hasAttic: nonRestrictedListing.hasAttic,
    propertyDisturber: getPropertyDisturberVariants({
      imageDisturbers: nonRestrictedListing.imageDisturbers,
    }),
  };
};

export const getCookiesConsentContext = () => {
  if (typeof window === 'undefined') {
    return getConsentDocumentContext({
      vendors_consent: '',
      vendors_consent_denied: '',
    });
  }
  return getConsentDocumentContext({
    vendors_consent: window.didomiState?.didomiVendorsRawConsent,
    vendors_consent_denied: window.didomiState?.didomiVendorsRawConsentDenied,
  });
};

export const mapFormDataToUserContext = (form: InputContact): Parameters<typeof getUserContext>[0] => {
  return {
    email: form.email,
    phoneNumber: form.phoneNumber,
    newsletterOptIn: form.marketingConsent?.country,
    newsletterOptInShop: form.marketingConsent?.shop,
    newsletterOptInPrivateOffice: form.marketingConsent?.privateOffice,
    evFinanceOptin: form.marketingConsent?.financing,
  };
};

export const mapPropertyToLocationContext = (listing: GenericExposeListing): Parameters<typeof getLocationContext>[0] => {
  return {
    country: listing.addressComponents.find((component) => component.placeType === PlaceType.country)?.text,
    countryCode: listing.countryAlpha2,
    region: listing.addressComponents.find((component) => component.placeType === PlaceType.administrative_area_level_1)?.text,
    city: listing.addressComponents.find((component) => component.placeType === PlaceType.locality)?.text,
    district: listing.addressComponents.find((component) => component.placeType === PlaceType.sublocality)?.text,
    zipCode: listing.addressComponents.find((component) => component.placeType === PlaceType.postal_code)?.text,
    placeId: getPlaceIdForLocationContext('placeIds' in listing ? (listing.placeIds ?? []) : []),
  };
};

export const getShopContextCommonValues = ({
  listing,
  shop,
}: {
  listing: Listing;
  shop?: MasterDataShopResponse;
}): Parameters<typeof getShopContext>[0] => ({
  ...(listing.trackingData?.masterDataShopID && {
    office_id: listing.trackingData.masterDataShopID,
  }),
  ...((shop?.id ?? listing.trackingData?.masterDataShopID) && {
    shop_id: shop?.id ?? listing.trackingData?.masterDataShopID,
  }),
  ...((shop?.lipaId ?? listing.trackingData?.masterDataLicensePartnerID) && {
    license_id: shop?.lipaId ?? listing.trackingData?.masterDataLicensePartnerID,
  }),
  business_division_name: listing.businessArea,
});
